import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCreateFollowUpPlan,
  useFollowUpPlanList,
  FOLLOW_UP_PLAN_STATUS_LABELS,
  type FollowUpPlanStatus,
} from '@/hooks/useFollowUp';
import {
  FOLLOW_UP_PERMISSIONS,
  canSeeFollowUpPlansMenu,
  hasPermission,
} from '@/lib/followUpPermissions';

const STATUS_OPTIONS: FollowUpPlanStatus[] = [
  'active',
  'paused',
  'completed',
  'cancelled',
];

const STATUS_BADGE_COLORS: Record<FollowUpPlanStatus, string> = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-amber-100 text-amber-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-zinc-200 text-zinc-700',
};

export default function FollowUpPlansPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { rows, total, page, limit, loading, error, fetchPlans } =
    useFollowUpPlanList();

  const [filterStatus, setFilterStatus] = useState<FollowUpPlanStatus | ''>('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [search, setSearch] = useState('');
  const [pageInput, setPageInput] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogKey, setCreateDialogKey] = useState(0);

  const canRead = canSeeFollowUpPlansMenu(permissions);
  const canCreate = hasPermission(permissions, FOLLOW_UP_PERMISSIONS.CREATE);

  useEffect(() => {
    if (!canRead) return;
    void fetchPlans({
      status: filterStatus || undefined,
      customerId: filterCustomer || undefined,
      search: search || undefined,
      page: pageInput,
      limit: 20,
    });
  }, [filterStatus, filterCustomer, search, pageInput, canRead, fetchPlans]);

  if (!canRead) {
    return <AccessDenied resource="随访计划" />;
  }

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">随访计划</h1>
          <p className="text-sm text-muted-foreground">
            按客户 / 服务用户 / 订单挂载的随访计划与节点。
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => {
              setCreateDialogKey((k) => k + 1);
              setCreateDialogOpen(true);
            }}
          >
            新建计划
          </Button>
        )}
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="filter-status">状态</Label>
            <select
              id="filter-status"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus((e.target.value || '') as FollowUpPlanStatus | '')
              }
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              <option value="">全部</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {FOLLOW_UP_PLAN_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-customer">客户 ID</Label>
            <Input
              id="filter-customer"
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              placeholder="例如 cust_1"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="filter-search">标题搜索</Label>
            <Input
              id="filter-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="标题关键字"
            />
          </div>
        </div>
      </Card>

      {error && (
        <Card className="border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left">
                <th className="px-3 py-2">标题</th>
                <th className="px-3 py-2">客户</th>
                <th className="px-3 py-2">状态</th>
                <th className="px-3 py-2">负责人</th>
                <th className="px-3 py-2">更新时间</th>
                <th className="px-3 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    加载中…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    暂无随访计划
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="px-3 py-2 font-medium">{row.title}</td>
                  <td className="px-3 py-2">{row.customerId}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-md px-2 py-0.5 text-xs ${STATUS_BADGE_COLORS[row.status]}`}
                    >
                      {FOLLOW_UP_PLAN_STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">{row.ownerUserId}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {new Date(row.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/follow-up/plans/${row.id}`)}
                    >
                      查看
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          共 {total} 条 · 第 {page} / {totalPages} 页
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pageInput <= 1}
            onClick={() => setPageInput((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pageInput >= totalPages}
            onClick={() => setPageInput((p) => p + 1)}
          >
            下一页
          </Button>
        </div>
      </div>

      {createDialogOpen && (
        <Card className="space-y-3 border-blue-200 bg-blue-50 p-4">
          <h2 className="text-base font-semibold">新建随访计划</h2>
          <CreateFollowUpPlanForm
            key={createDialogKey}
            onCreated={() => {
              setCreateDialogOpen(false);
              setPageInput(1);
              void fetchPlans({ page: 1, limit: 20 });
            }}
            onCancel={() => setCreateDialogOpen(false)}
          />
        </Card>
      )}
    </div>
  );
}

function CreateFollowUpPlanForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { createPlan, loading, error } = useCreateFollowUpPlan();
  const [customerId, setCustomerId] = useState('');
  const [title, setTitle] = useState('');
  const [objective, setObjective] = useState('');
  const [cadenceHint, setCadenceHint] = useState('');

  const submit = async () => {
    if (!customerId.trim() || !title.trim()) return;
    const plan = await createPlan({
      customerId: customerId.trim(),
      title: title.trim(),
      objective: objective.trim() || undefined,
      cadenceHint: cadenceHint.trim() || undefined,
    });
    if (plan) onCreated();
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="create-customer">客户 ID</Label>
        <Input
          id="create-customer"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          placeholder="客户主键，必填"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="create-title">标题</Label>
        <Input
          id="create-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={255}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="create-objective">目标</Label>
        <textarea
          id="create-objective"
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          maxLength={2000}
          rows={3}
          className="w-full rounded-md border bg-background p-2 text-sm"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="create-cadence">节奏</Label>
        <Input
          id="create-cadence"
          value={cadenceHint}
          onChange={(e) => setCadenceHint(e.target.value)}
          placeholder="如 monthly / quarterly"
          maxLength={64}
        />
      </div>
      {error && <div className="text-sm text-red-700">{error.message}</div>}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          取消
        </Button>
        <Button onClick={submit} disabled={loading}>
          {loading ? '提交中…' : '创建'}
        </Button>
      </div>
    </div>
  );
}
